import type { ReactNode } from 'react';
import { glossaryEntry } from '@/lib/education';
import { useSettingsStore } from '@/stores/settingsStore';
import { useUiStore } from '@/stores/uiStore';

interface TermHelpProps {
  termId: string;
  children?: ReactNode;
  className?: string;
}

/**
 * Inline, keyboard-accessible terminology help. Native `title` supplies an
 * immediate hover definition; selecting the term opens its full glossary
 * entry. Professional mode keeps the text but removes the visual underline.
 */
export function TermHelp({ termId, children, className = '' }: TermHelpProps) {
  const entry = glossaryEntry(termId);
  const experienceLevel = useSettingsStore((state) => state.experienceLevel);
  const openEducationCenter = useUiStore((state) => state.openEducationCenter);
  if (!entry) return children;

  return (
    <button
      type="button"
      title={`${entry.definition} Select for the full explanation.`}
      aria-label={`${entry.term}: ${entry.definition}`}
      onClick={() => openEducationCenter(termId)}
      className={`inline cursor-help text-inherit ${
        experienceLevel === 'guided'
          ? 'decoration-info/70 underline decoration-dotted underline-offset-2'
          : ''
      } ${className}`}
    >
      {children ?? entry.abbreviation ?? entry.term}
    </button>
  );
}
